<template>
  <v-card class="version-navigator" dark flat>
    <v-card-text class="pa-3">
      <div class="d-flex align-center">
        <v-btn v-if="canClassify" color="primary" class="mr-4" small @click="$emit('classification')">
          {{ $t('markup.classification') }}
        </v-btn>

        <v-spacer></v-spacer>

        <!-- Center: Version Navigation -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            :disabled="!canGoPrevious"
            @click="goToPrevious"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div class="version-list mx-2">
            <v-chip-group
              v-model="selectedIndex"
              active-class="primary--text"
              mandatory
            >
              <v-chip
                v-for="version in versions"
                :key="version.markup_session_node_id"
                :value="version.markup_session_node_id"
                small
                :color="versionColor(version)"
                :outlined="currentVersionId !== version.markup_session_node_id"
                @click="selectVersion(version)"
                class="mx-1"
              >
                <v-icon small left>{{ versionIcon(version) }}</v-icon>
                v{{ version.version }}
                <v-badge
                  v-if="version.markups_count > 0"
                  :content="version.markups_count"
                  color="red"
                  inline
                  class="ml-1"
                ></v-badge>
              </v-chip>
            </v-chip-group>
          </div>

          <v-btn
            icon
            small
            :disabled="!canGoNext"
            @click="goToNext"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          @click="$emit('create-markup')"
          :disabled="!canCreateMarkup"
          class="mr-4"
          small
        >
          <v-icon left>mdi-pin</v-icon>
          {{ $t('markup.create_markup') }}
        </v-btn>

        <!-- Right side: Zoom Controls -->
        <v-chip label dark class="d-flex align-center py-1" color="grey darken-4">
          <v-btn
            icon
            @click="$emit('zoom-out')"
          >
            <v-icon>mdi-magnify-minus-outline</v-icon>
          </v-btn>

          <span class="mx-2">{{ zoomLevel }}%</span>

          <v-btn
            icon
            @click="$emit('zoom-in')"
          >
            <v-icon>mdi-magnify-plus-outline</v-icon>
          </v-btn>

          <v-divider
            class="custom-vertical-divider"
            vertical
          ></v-divider>

          <v-btn
            icon
            @click="$emit('toggle-magnifier')"
            :color="magnifierActive ? 'primary' : ''"
            :class="{ 'magnifier-active': magnifierActive }"
            class="ml-1 mr-1"
          >
            <v-icon>mdi-crop-free</v-icon>
          </v-btn>

          <v-divider
            class="custom-vertical-divider"
            vertical
          ></v-divider>

          <v-btn
            icon
            @click="$emit('toggle-markups')"
            :color="markupsVisible ? 'primary' : ''"
            :class="{ 'markups-active': markupsVisible }"
            class="ml-2"
          >
            <v-icon>{{ markupsVisible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
          </v-btn>
        </v-chip>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'VersionNavigator',

  props: {
    canClassify: {
      type: Boolean,
      default: false
    },
    versions: {
      type: Array,
      required: true
    },
    currentVersionId: {
      type: Number,
      required: true
    },
    zoomLevel: {
      type: Number,
      default: 100
    },
    magnifierActive: {
      type: Boolean,
      default: false
    },
    markupsVisible: {
      type: Boolean,
      default: true
    },
    canCreateMarkup: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      selectedIndex: null
    }
  },

  computed: {
    currentVersionIndex() {
      return this.versions.findIndex(v => v.markup_session_node_id === this.currentVersionId)
    },

    canGoPrevious() {
      return this.currentVersionIndex > 0
    },

    canGoNext() {
      return this.currentVersionIndex < this.versions.length - 1
    },

    currentVersion() {
      return this.versions[this.currentVersionIndex]
    },

    currentVersionInfo() {
      if (!this.currentVersion) return ''
      const date = new Date(this.currentVersion.created_at)
      return `${this.currentVersion.file_info.name} - ${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }
  },

  mounted() {
    this.selectedIndex = this.currentVersionId
  },

  watch: {
    currentVersionId(newVal) {
      this.selectedIndex = newVal
    }
  },

  methods: {
    selectVersion(version) {
      this.$emit('version-change', version)
    },

    goToPrevious() {
      if (this.canGoPrevious) {
        const previousVersion = this.versions[this.currentVersionIndex - 1]
        this.selectVersion(previousVersion)
      }
    },

    goToNext() {
      if (this.canGoNext) {
        const nextVersion = this.versions[this.currentVersionIndex + 1]
        this.selectVersion(nextVersion)
      }
    },

    versionColor(version) {
      const colors = {
        pending: '',
        approved: 'success',
        rejected: 'error',
        cancelled: 'grey'
      }
      return colors[version.status] || ''
    },

    versionIcon(version) {
      const icons = {
        pending: 'mdi-clock-outline',
        approved: 'mdi-check-circle',
        rejected: 'mdi-close-circle',
        cancelled: 'mdi-cancel'
      }
      return icons[version.status] || 'mdi-file'
    }
  }
}
</script>

<style scoped>
.version-navigator {
  background-color: #424242;
}

.version-list {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
}

.version-list::-webkit-scrollbar {
  height: 4px;
}

.version-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
}

.custom-vertical-divider {
  border-width: 0 thin 0 0 !important;
  border-color: white !important;
}
</style>
